import { useCallback, useEffect, useState } from 'react';
import { mapConfigService } from '../services/mapConfigService';
import type { MapConfig } from '../services/mapConfigService';

const MAX_TIMER_DELAY_MS = 2_147_000_000;

export function useMapConfig(): {
  config: MapConfig | null;
  isLoading: boolean;
  error: Error | null;
  refresh: (forceRefresh?: boolean) => Promise<void>;
} {
  const [config, setConfig] = useState<MapConfig | null>(() => mapConfigService.getCachedMapConfig());
  const [isLoading, setIsLoading] = useState(() => !mapConfigService.getCachedMapConfig());
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async (forceRefresh = false) => {
    if (!config) setIsLoading(true);
    try {
      const nextConfig = await mapConfigService.getMapConfig({ forceRefresh });
      setConfig(nextConfig);
      setError(null);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error('Unable to load map configuration'));
    } finally {
      setIsLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (!config) void refresh();
  }, [config, refresh]);

  useEffect(() => {
    if (!config) return undefined;
    const timer = window.setTimeout(
      () => { void refresh(true); },
      Math.min(mapConfigService.getRenewalDelay(config), MAX_TIMER_DELAY_MS),
    );
    return () => window.clearTimeout(timer);
  }, [config, refresh]);

  return { config, isLoading, error, refresh };
}
